import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  Account,
  ActivateConfigEvent,
  ActivateVenueEvent,
  AddConfigEvent,
  AssignConfigEvent,
  DeactivateConfigEvent,
  DeactivateVenueEvent,
  EVENT_TYPES,
  OverrideVenueEvent,
  PutVenueAccountsEvent,
  RemoveAccountsEvent,
  RemoveConfigEvent,
  RemoveVenueAccountsEvent,
  SetAccountsEvent,
  SetVenueAccountsEvent,
  UniverseBuilder,
  UpdateConfigEvent,
} from '@baemingo/integrations-config';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import * as actions from 'actions';
import { useSelector } from 'Hooks';

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
const Context = React.createContext(new UniverseBuilder().serialize());

export const IntegrationsConfigsUniverseProvider: React.FunctionComponent = ({ children }) => {
  const companyId = useSelector((state) => state.company);
  // A local universe
  const universe = useRef(new UniverseBuilder());

  // The current index of which event we've added to the universe from redux
  const index = useRef(0);

  // Get events from state. Events are added via UI actions
  const events = useSelector((state) => state.integrationsConfigEvents);

  // Get snapshot from state. If this changes we override our current universe
  const snapshot = useSelector((state) => state.integrationsConfigSnapshot.snapshot);

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState(universe.current.serialize());

  useEffect(() => {
    // Start by removing any events we've already added, then add the event
    events.slice(index.current).forEach((event) => universe.current.addEvent(event));

    // Update our active index
    index.current = events.length;

    // Save to state and trigger a re-render
    setState(universe.current.serialize());
  }, [events]);

  useEffect(() => {
    if (!snapshot) return;
    universe.current = new UniverseBuilder({ ...snapshot });
    setState(universe.current.serialize());
  }, [snapshot]);

  useEffect(() => {
    // Reset universe on switching company
    // The ref will persist the previous state, so it has to be reset as well
    universe.current = new UniverseBuilder();
    setState(new UniverseBuilder().serialize());
    index.current = 0;
  }, [companyId]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useIntegrationsConfigsUniverse = () => {
  return useContext(Context);
};

export const useIntegrationsConfigsApi = () => {
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.company);
  const userId = useSelector((state) => state.userId);

  const generateEventMeta = () => ({
    createdAt: new Date().toISOString(),
    eventId: uuid(),
    sentAt: null,
    companyId: companyId,
    userId,
  });

  const API = {
    addConfig: (eventData: AddConfigEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.ADD_CONFIG,
          configId: uuid(),
          configName: eventData.configName,
          accountingProvider: eventData.accountingProvider,
          apiKey: eventData.apiKey ?? '',
          recipientEmail: eventData.recipientEmail ?? '',
          costCenter: eventData.costCenter ?? null,
          endOfDayTime: eventData.endOfDayTime ?? '05:30',
          voucherSeries: eventData.voucherSeries,
          genericVoucherSeries: eventData.genericVoucherSeries,
          voucherText: eventData.voucherText,
          sendPdf: eventData.sendPdf,
          addTimeAndCustomerNameToInvoiceRows: eventData.addTimeAndCustomerNameToInvoiceRows,
          ...(eventData.invoicesConfig && {
            invoicesConfig: eventData.invoicesConfig,
          }),
          ...generateEventMeta(),
        }),
      );
    },

    updateConfig: (eventData: Omit<UpdateConfigEvent, 'type'>) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          ...eventData,
          ...generateEventMeta(),
          type: EVENT_TYPES.UPDATE_CONFIG,
        }),
      );
    },

    assignConfig: (eventData: AssignConfigEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.ASSIGN_CONFIG,
          configId: eventData.configId,
          venueId: eventData.venueId,
          moveOverrides: eventData.moveOverrides || false,
          ...generateEventMeta(),
        }),
      );
    },

    overrideVenue: (eventData: OverrideVenueEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          //@ts-ignore
          type: EVENT_TYPES.OVERRIDE_VENUE,
          //@ts-ignore
          venueId: eventData.venueId,
          ...eventData,
          ...generateEventMeta(),
        }),
      );
    },

    putAccounts: (eventData: { configId: string; accounts: Account[] }) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.PUT_ACCOUNTS,
          configId: eventData.configId,
          accounts: eventData.accounts,
          ...generateEventMeta(),
        }),
      );
    },

    setAccounts: (eventData: SetAccountsEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.SET_ACCOUNTS,
          configId: eventData.configId,
          accounts: eventData.accounts,
          ...generateEventMeta(),
        }),
      );
    },

    putVenueAccounts: (eventData: PutVenueAccountsEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.PUT_VENUE_ACCOUNTS,
          configId: eventData.configId,
          venueId: eventData.venueId,
          accounts: eventData.accounts,
          ...generateEventMeta(),
        }),
      );
    },

    setVenueAccounts: (eventData: SetVenueAccountsEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.SET_VENUE_ACCOUNTS,
          configId: eventData.configId,
          venueId: eventData.venueId,
          accounts: eventData.accounts,
          ...generateEventMeta(),
        }),
      );
    },

    activateConfig: (eventData: ActivateConfigEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.ACTIVATE_CONFIG,
          configId: eventData.configId,
          activateVenues: eventData.activateVenues,
          ...generateEventMeta(),
        }),
      );
    },

    deactivateConfig: (eventData: DeactivateConfigEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.DEACTIVATE_CONFIG,
          configId: eventData.configId,
          ...generateEventMeta(),
        }),
      );
    },

    activateVenue: (eventData: ActivateVenueEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.ACTIVATE_VENUE,
          configId: eventData.configId,
          venueId: eventData.venueId,
          ...generateEventMeta(),
        }),
      );
    },

    deactivateVenue: (eventData: DeactivateVenueEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.DEACTIVATE_VENUE,
          configId: eventData.configId,
          venueId: eventData.venueId,
          ...generateEventMeta(),
        }),
      );
    },

    removeAccounts: (eventData: RemoveAccountsEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.REMOVE_ACCOUNTS,
          configId: eventData.configId,
          accountType: eventData.accountType,
          keys: eventData.keys,
          ...generateEventMeta(),
        }),
      );
    },

    removeVenueAccounts: (eventData: RemoveVenueAccountsEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.REMOVE_VENUE_ACCOUNTS,
          configId: eventData.configId,
          accountType: eventData.accountType,
          venueId: eventData.venueId,
          keys: eventData.keys,
          ...generateEventMeta(),
        }),
      );
    },

    removeConfig: (eventData: RemoveConfigEvent) => {
      dispatch(
        actions.addIntegrationsConfigsEvent({
          type: EVENT_TYPES.REMOVE_CONFIG,
          configId: eventData.configId,
          ...generateEventMeta(),
        }),
      );
    },
  };
  return API;
};
